"use client";

import { JSX, useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { fmtNum } from "@/lib/format";
import { useViewerLanguage } from "@/lib/i18n";
import { useTween } from "@/lib/hooks/use-tween";
import { AreaChart, Area } from "@/components/charts/area-chart";
import { Grid } from "@/components/charts/grid";
import { XAxis } from "@/components/charts/x-axis";
import { YAxis } from "@/components/charts/y-axis";
import { ChartTooltip } from "@/components/charts/tooltip/chart-tooltip";
import { ReferenceLine } from "@/components/charts/reference-line";
import { useChartHover } from "@/components/charts/chart-context";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
  LegendValue,
} from "@/components/charts/legend";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { MODULE_LABEL_WRAP } from "./module-label-budget";
import type { ModuleBigNumberChartData } from "@/types/modules";
import { importsTokenScope } from "./imports-token-scope";

const CHART_HEIGHT = 220;

interface ChartRow {
  x: string;
  value: number;
  value2: number | null;
}

type BigNumberData = ModuleBigNumberChartData & {
  seriesB?: number[];
  seriesBLabel?: string;
};

function HoverIndexReporter({
  onChange,
}: {
  onChange: (index: number | null) => void;
}) {
  const { tooltipData } = useChartHover();
  const idx = tooltipData?.index ?? null;
  useEffect(() => {
    onChange(idx);
  }, [idx, onChange]);
  return null;
}

export default function ModuleBigNumberChart({
  data,
  grayscale,
  accent,
  className,
}: {
  data: ModuleBigNumberChartData;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const { t, locale, intlLocale } = useViewerLanguage();
  const source = data as BigNumberData;
  const series = data.series ?? [];
  const baseline = data.baseline;
  const seriesB = source.seriesB;
  const seriesBLabel = source.seriesBLabel ?? t("Comparison");
  const metricLabel = data.metricLabel ?? t("Metric");
  const description = data.description;
  const unit = data.unit ?? "";
  const periodLabel = data.periodLabel ?? t("Period");
  const periodLabelPlural = data.periodLabelPlural ?? t("periods");
  const seriesLabel = data.seriesLabel ?? t("Series");
  const baselineLabel = data.baselineLabel ?? t("Baseline");
  const deltaUnit = data.deltaUnit ?? "";

  const [hover, setHover] = useState<number | null>(null);
  const [legHover, setLegHover] = useState<number | null>(null);

  const last = Math.max(0, series.length - 1);
  const current = hover ?? last;
  const shown = useTween(series[current] ?? 0, {
    from: 0,
    duration: hover === null ? 900 : 300,
  });

  const hasSeriesB = Array.isArray(seriesB) && seriesB.length > 0;
  const hasBaseline = baseline !== undefined;

  // Two series are listed in the legend, so two areas have to be drawn. A
  // baseline is a single number, which becomes the flat second series.
  const secondary = useMemo<number[] | null>(() => {
    if (hasSeriesB && seriesB) {
      const lastB = seriesB[seriesB.length - 1] ?? 0;
      return series.map((_, i) => seriesB[i] ?? lastB);
    }
    if (hasBaseline) {
      return series.map(() => baseline as number);
    }
    return null;
  }, [hasSeriesB, hasBaseline, series, seriesB, baseline]);

  const secondaryLabel = hasSeriesB ? seriesBLabel : baselineLabel;

  const chartData = useMemo<ChartRow[]>(
    () =>
      series.map((v, i) => ({
        x: `${periodLabel} ${i + 1}`,
        value: v,
        value2: secondary ? (secondary[i] ?? null) : null,
      })),
    [series, periodLabel, secondary],
  );
  const diff = hasBaseline ? series[current] - baseline : 0;
  const deltaPrev = current > 0 ? series[current] - series[current - 1] : null;
  const avg = series.length
    ? series.reduce((a, v) => a + v, 0) / series.length
    : 0;
  const sign = (v: number) => (v >= 0 ? "+" : "");
  const signed = (v: number) => `${sign(v)}${fmtNum(v, 0, intlLocale)} ${deltaUnit}`.trim();
  const periodInSentence =
    locale === "de" ? periodLabel : periodLabel.toLocaleLowerCase(intlLocale);

  const legendItems = [
    {
      id: "series",
      label: seriesLabel,
      color: "var(--chart-1)",
      value: `${series[last]} ${unit}`.trim(),
    },
    ...(secondary
      ? [
          {
            id: "secondary",
            label: secondaryLabel,
            color: "var(--chart-3)",
            value: `${secondary[last] ?? 0} ${unit}`.trim(),
          },
        ]
      : []),
    ...(hasSeriesB && hasBaseline
      ? [
          {
            id: "baseline",
            label: baselineLabel,
            color: "var(--chart-4)",
            value: `${baseline} ${unit}`.trim(),
            dashed: true,
          },
        ]
      : []),
  ];

  return (
    <div
      className={cn("w-full", className)}
      style={importsTokenScope(grayscale, accent)}
    >
      <div className={`text-sm text-[var(--muted)] ${MODULE_LABEL_WRAP.row}`}>
        {stripInlineMarkdown(metricLabel)}{" "}
        {hover === null
          ? t("now")
          : t("in {period} {index}", { period: periodLabel, index: current + 1 })}
      </div>
      <div className="mt-2 text-7xl font-medium tracking-tighter tabular-nums leading-none">
        {fmtNum(Math.round(shown), 0, intlLocale)} {unit}
      </div>
      {description && (
        <p className="mt-3 max-w-md text-[15px] leading-relaxed text-[var(--muted)]">
          {stripInlineMarkdown(description)}
        </p>
      )}
      <div className="mt-2 h-5 text-sm text-[var(--muted)]">
        {hover !== null && deltaPrev !== null ? (
          <span className="underline decoration-dotted underline-offset-4">
            {t("{delta} vs. previous {period}", {
              delta: signed(deltaPrev),
              period: periodInSentence,
            })}
          </span>
        ) : hasBaseline ? (
          <span className="underline decoration-dotted underline-offset-4">
            {t("{delta} vs. {baseline}", {
              delta: signed(diff),
              baseline: baselineLabel,
            })}
          </span>
        ) : null}
      </div>

      <div className="mt-6 flex flex-wrap items-baseline justify-between gap-3 text-sm text-[var(--muted)]">
        <div>
          {t("Change since {period} {index}", { period: periodLabel, index: 1 })}{" "}
          <span className="font-medium tabular-nums text-[var(--fg)]">
            {signed(series[last] - series[0])}
          </span>
        </div>
        <div>
          {t("Average over {total} {periods}", {
            total: series.length,
            periods: periodLabelPlural,
          })}{" "}
          <span className="font-medium tabular-nums text-[var(--fg)]">
            {fmtNum(avg, 0, intlLocale)} {unit}
          </span>
        </div>
      </div>

      <div className="mt-3" style={{ height: CHART_HEIGHT }}>
        <AreaChart
          data={chartData as unknown as Record<string, unknown>[]}
          xDataKey="x"
          xScaleKind="band"
          margin={{ top: 12, right: 16, bottom: 32, left: 40 }}
          aspectRatio="auto"
          className="h-full"
        >
          <Grid horizontal vertical={false} />
          <YAxis />
          {secondary && (
            <Area
              dataKey="value2"
              stroke="var(--chart-3)"
              strokeWidth={2}
              fill="var(--chart-3)"
              fillOpacity={0.14}
              gradientToOpacity={0}
              fadeEdges
            />
          )}
          <Area
            dataKey="value"
            stroke="var(--chart-1)"
            strokeWidth={2}
            fill="var(--chart-1)"
            fillOpacity={0.2}
            gradientToOpacity={0}
            fadeEdges
          />
          {hasSeriesB && hasBaseline && (
            <ReferenceLine
              orientation="horizontal"
              value={baseline}
              stroke="var(--chart-4)"
              strokeStyle="dashed"
              strokeWidth={1.6}
              strokeDasharray="5,4"
              fadeEdges
            />
          )}
          <XAxis />
          <ChartTooltip
            showDatePill
            showCrosshair
            showDots
            rows={(point) => {
              const rows: {
                color: string;
                label: string;
                value: string;
              }[] = [
                {
                  color: "var(--chart-1)",
                  label: seriesLabel,
                  value: `${point.value} ${unit}`.trim(),
                },
              ];
              if (secondary) {
                const other = Number(point.value2 ?? 0);
                rows.push({
                  color: "var(--chart-3)",
                  label: secondaryLabel,
                  value: `${other} ${unit}`.trim(),
                });
                const d = (point.value as number) - other;
                rows.push({
                  color: "var(--muted-foreground)",
                  label: t("Difference"),
                  value: signed(d),
                });
              }
              return rows;
            }}
          />
          <HoverIndexReporter onChange={setHover} />
        </AreaChart>
      </div>

      <div className="mt-3 border-t border-dashed border-[var(--border-dashed-soft)] pt-4">
        <Legend
          items={legendItems}
          hoveredIndex={legHover}
          onHoverChange={setLegHover}
          className="flex flex-row gap-1"
        >
          <LegendItem className="flex shrink-0 items-center gap-2 px-2 py-1">
            <LegendMarker />
            <LegendLabel />
            <LegendValue />
          </LegendItem>
        </Legend>
      </div>
    </div>
  );
}
